import { z } from 'zod/v3'

import type { ImageValue, LinkForm } from '@/components/retail/home-form-model'
import type { ShopSiteSettingKind } from '@/lib/api/shop-site-settings-api'
import { linkIn, linkOut } from '@/components/retail/home-form-model'
import { m } from '@/lib/paraglide/messages'

// Mô tả trường từng kind cài đặt website (khớp server
// usecase/shop/site_settings_types.go). Màn quản trị render form từ bảng này;
// tên ô trong form = đường dẫn trong data, nên lỗi server params.field
// (vd data.usp_items.1) gắn đúng ô.

type Label = () => string

export type SettingField =
    | {
          k: string
          t: 'line'
          max: number
          label: Label
          required?: boolean
          hint?: Label
      }
    | { k: string; t: 'text'; max: number; label: Label; hint?: Label }
    | { k: string; t: 'image'; label: Label }
    | { k: string; t: 'link'; label: Label; required?: boolean }
    | { k: string; t: 'icon'; label: Label }
    | { k: string; t: 'lines'; max: number; itemMax: number; label: Label }
    | {
          k: string
          t: 'list'
          max: number
          label: Label
          item: (p: { n: string }) => string
          fields: SettingField[]
      }
    | { k: string; t: 'group'; label: Label; fields: SettingField[] }

/** Khoá icon có sẵn ở website (components/site/icons.tsx). */
export const SITE_ICON_KEYS = [
    'leaf',
    'leaf_simple',
    'truck',
    'qr_pay',
    'shield_check',
    'return',
    'gift',
    'phone',
    'star',
    'lock',
    'info',
    'clock',
] as const

const hintYear = () => m.siteSettings_hint_year({ token: '{year}' })
const hintName = () => m.siteSettings_hint_name({ token: '{name}' })
const hintStoreSystem = () =>
    m.siteSettings_hint_store_system({
        branches: '{branches}',
        stores: '{stores}',
    })

const line = (
    k: string,
    max: number,
    label: Label,
    extra?: { required?: boolean; hint?: Label },
): SettingField => ({
    k,
    t: 'line',
    max,
    label,
    ...extra,
})

const iconList = (k: string, max: number, label: Label): SettingField => ({
    k,
    t: 'list',
    max,
    label,
    item: m.siteSettings_item,
    fields: [
        { k: 'icon', t: 'icon', label: m.siteSettings_f_icon },
        line('title', 60, m.siteSettings_f_title, { required: true }),
        line('text', 120, m.siteSettings_f_text),
    ],
})

const textList = (k: string, max: number, label: Label): SettingField => ({
    k,
    t: 'list',
    max,
    label,
    item: m.siteSettings_item,
    fields: [
        line('title', 60, m.siteSettings_f_title, { required: true }),
        line('text', 160, m.siteSettings_f_text),
        line('href', 500, m.siteSettings_f_href, {
            hint: m.siteSettings_hint_href,
        }),
    ],
})

const navList = (k: string, max: number, label: Label): SettingField => ({
    k,
    t: 'list',
    max,
    label,
    item: m.siteSettings_item,
    fields: [
        line('label', 60, m.siteSettings_f_label, { required: true }),
        { k: 'link', t: 'link', label: m.siteSettings_f_link, required: true },
    ],
})

export const SITE_SETTING_FIELDS: Record<ShopSiteSettingKind, SettingField[]> =
    {
        brand: [
            line('short_name', 60, m.siteSettings_brand_short_name),
            line('legal_name', 120, m.siteSettings_brand_legal_name),
            line('tagline', 160, m.siteSettings_brand_tagline),
            line('logo_subtitle', 60, m.siteSettings_brand_logo_subtitle),
            line('manufacturer', 160, m.siteSettings_brand_manufacturer),
            line('website_url', 500, m.siteSettings_brand_website_url),
            {
                k: 'badges',
                t: 'list',
                max: 4,
                label: m.siteSettings_brand_badges,
                item: m.siteSettings_brand_badge,
                fields: [
                    { k: 'image', t: 'image', label: m.siteSettings_f_image },
                    line('line1', 60, m.siteSettings_brand_badge_line1, {
                        required: true,
                    }),
                    line('line2', 80, m.siteSettings_brand_badge_line2),
                    line('title', 120, m.siteSettings_brand_badge_title),
                    line('alt', 120, m.siteSettings_brand_badge_alt),
                    line('href', 500, m.siteSettings_f_href, {
                        hint: m.siteSettings_hint_href,
                    }),
                ],
            },
            {
                k: 'about_blurb',
                t: 'group',
                label: m.siteSettings_brand_about_blurb,
                fields: [
                    line('title', 120, m.siteSettings_f_title),
                    {
                        k: 'body',
                        t: 'text',
                        max: 1000,
                        label: m.siteSettings_f_body,
                    },
                ],
            },
        ],
        header: [
            {
                k: 'usp_items',
                t: 'lines',
                max: 3,
                itemMax: 80,
                label: m.siteSettings_header_usp_items,
            },
            {
                k: 'usp_items_mobile',
                t: 'lines',
                max: 4,
                itemMax: 40,
                label: m.siteSettings_header_usp_items_mobile,
            },
            line(
                'search_placeholder',
                120,
                m.siteSettings_header_search_placeholder,
            ),
            line(
                'search_placeholder_mobile',
                80,
                m.siteSettings_header_search_placeholder_mobile,
            ),
            line(
                'store_link_label',
                60,
                m.siteSettings_header_store_link_label,
            ),
            navList('extra_links', 4, m.siteSettings_header_extra_links),
        ],
        footer: [
            iconList('usp_items', 4, m.siteSettings_footer_usp_items),
            line('slogan', 120, m.siteSettings_footer_slogan),
            line('copyright', 200, m.siteSettings_footer_copyright, {
                hint: hintYear,
            }),
            line(
                'copyright_short',
                120,
                m.siteSettings_footer_copyright_short,
                { hint: hintYear },
            ),
            navList('policy_links', 12, m.siteSettings_footer_policy_links),
            navList('about_links', 8, m.siteSettings_footer_about_links),
        ],
        socials: [
            {
                k: 'items',
                t: 'list',
                max: 8,
                label: m.siteSettings_socials_items,
                item: m.siteSettings_item,
                fields: [
                    line('label', 40, m.siteSettings_f_label, {
                        required: true,
                    }),
                    line('url', 500, m.siteSettings_f_url, { required: true }),
                ],
            },
            line('zalo_oa_id', 40, m.siteSettings_socials_zalo_oa_id),
        ],
        seo: [
            line('site_name', 60, m.siteSettings_seo_site_name),
            line('default_title', 120, m.siteSettings_seo_default_title),
            line('title_template', 120, m.siteSettings_seo_title_template, {
                hint: m.siteSettings_hint_title_template,
            }),
            {
                k: 'default_description',
                t: 'text',
                max: 300,
                label: m.siteSettings_seo_default_description,
            },
            {
                k: 'default_og_image',
                t: 'image',
                label: m.siteSettings_seo_default_og_image,
            },
            line('home_title', 120, m.siteSettings_seo_home_title),
            {
                k: 'home_description',
                t: 'text',
                max: 300,
                label: m.siteSettings_seo_home_description,
            },
            {
                k: 'home_og_image',
                t: 'image',
                label: m.siteSettings_seo_home_og_image,
            },
            line('home_h1', 160, m.siteSettings_seo_home_h1),
            {
                k: 'news_description',
                t: 'text',
                max: 300,
                label: m.siteSettings_seo_news_description,
            },
            {
                k: 'news_intro',
                t: 'text',
                max: 500,
                label: m.siteSettings_seo_news_intro,
            },
            {
                k: 'products_description',
                t: 'text',
                max: 300,
                label: m.siteSettings_seo_products_description,
            },
            {
                k: 'contact_description',
                t: 'text',
                max: 300,
                label: m.siteSettings_seo_contact_description,
            },
            line(
                'category_description_tpl',
                300,
                m.siteSettings_seo_category_description_tpl,
                { hint: hintName },
            ),
            line(
                'product_description_tpl',
                300,
                m.siteSettings_seo_product_description_tpl,
                { hint: hintName },
            ),
        ],
        product_page: [
            iconList('perks', 4, m.siteSettings_product_perks),
            textList('buy_benefits', 4, m.siteSettings_product_buy_benefits),
            textList(
                'buy_benefits_mobile',
                4,
                m.siteSettings_product_buy_benefits_mobile,
            ),
            line('phone_cta_label', 60, m.siteSettings_product_phone_cta_label),
            line(
                'phone_cta_label_mobile',
                40,
                m.siteSettings_product_phone_cta_label_mobile,
            ),
            line(
                'shipping_notice',
                200,
                m.siteSettings_product_shipping_notice,
            ),
            line(
                'shipping_notice_short',
                120,
                m.siteSettings_product_shipping_notice_short,
            ),
            line('buy_note', 120, m.siteSettings_product_buy_note),
            line(
                'out_of_stock_note',
                200,
                m.siteSettings_product_out_of_stock_note,
            ),
            line(
                'out_of_stock_cta_label',
                60,
                m.siteSettings_product_out_of_stock_cta_label,
            ),
            line(
                'default_shelf_life',
                120,
                m.siteSettings_product_default_shelf_life,
            ),
        ],
        checkout: [
            line('trust_line', 300, m.siteSettings_checkout_trust_line),
            {
                k: 'terms_note',
                t: 'text',
                max: 500,
                label: m.siteSettings_checkout_terms_note,
            },
            {
                k: 'terms_note_fee_later',
                t: 'text',
                max: 500,
                label: m.siteSettings_checkout_terms_note_fee_later,
            },
            line(
                'payment_cod_title',
                80,
                m.siteSettings_checkout_payment_cod_title,
            ),
            line(
                'payment_cod_text',
                200,
                m.siteSettings_checkout_payment_cod_text,
            ),
            line(
                'payment_qr_title',
                80,
                m.siteSettings_checkout_payment_qr_title,
            ),
            line(
                'payment_qr_text',
                200,
                m.siteSettings_checkout_payment_qr_text,
            ),
            {
                k: 'support_reasons',
                t: 'group',
                label: m.siteSettings_checkout_support_reasons,
                fields: [
                    {
                        k: 'waiting',
                        t: 'lines',
                        max: 8,
                        itemMax: 80,
                        label: m.siteSettings_checkout_reasons_waiting,
                    },
                    {
                        k: 'shipping',
                        t: 'lines',
                        max: 8,
                        itemMax: 80,
                        label: m.siteSettings_checkout_reasons_shipping,
                    },
                    {
                        k: 'done',
                        t: 'lines',
                        max: 8,
                        itemMax: 80,
                        label: m.siteSettings_checkout_reasons_done,
                    },
                ],
            },
        ],
        home_extra: [
            textList('support_items', 6, m.siteSettings_home_support_items),
            line(
                'hotline_cta_label',
                60,
                m.siteSettings_home_hotline_cta_label,
            ),
            line('advice_title', 120, m.siteSettings_home_advice_title),
            {
                k: 'advice_body',
                t: 'text',
                max: 300,
                label: m.siteSettings_home_advice_body,
            },
            {
                k: 'login_pitch',
                t: 'text',
                max: 300,
                label: m.siteSettings_home_login_pitch,
            },
            line('story_badge_text', 120, m.siteSettings_home_story_badge_text),
            line(
                'store_system_subtitle',
                300,
                m.siteSettings_home_store_system_subtitle,
                { hint: hintStoreSystem },
            ),
            {
                k: 'brand_banner',
                t: 'group',
                label: m.siteSettings_home_brand_banner,
                fields: [
                    { k: 'image', t: 'image', label: m.siteSettings_f_image },
                    line('title', 120, m.siteSettings_f_title),
                    line('subtitle', 200, m.siteSettings_f_subtitle),
                    line('cta_label', 40, m.siteSettings_f_cta_label),
                    { k: 'link', t: 'link', label: m.siteSettings_f_link },
                ],
            },
        ],
        ocop: [
            line('issuer_national', 160, m.siteSettings_ocop_issuer_national),
            line(
                'issuer_provincial',
                160,
                m.siteSettings_ocop_issuer_provincial,
            ),
            line('province_label', 60, m.siteSettings_ocop_province_label),
            line('cta_label', 60, m.siteSettings_ocop_cta_label),
            { k: 'cta_link', t: 'link', label: m.siteSettings_ocop_cta_link },
            line('mobile_title', 120, m.siteSettings_ocop_mobile_title),
            line('mobile_subtitle', 160, m.siteSettings_ocop_mobile_subtitle),
        ],
        miniapp: [
            line('login_title', 120, m.siteSettings_miniapp_login_title),
            line('login_subtitle', 200, m.siteSettings_miniapp_login_subtitle),
            {
                k: 'login_benefits',
                t: 'lines',
                max: 5,
                itemMax: 120,
                label: m.siteSettings_miniapp_login_benefits,
            },
            {
                k: 'login_hero_image',
                t: 'image',
                label: m.siteSettings_miniapp_login_hero_image,
            },
        ],
    }

// --- chuyển đổi data ↔ form ---

type Obj = Record<string, unknown>

/** Dòng của ô danh sách chữ: object để useFieldArray có id ổn định. */
export interface LineRow {
    v: string
}

const obj = (v: unknown): Obj =>
    v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {}
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const str = (v: unknown) => (typeof v === 'string' ? v : '')

function imageIn(v: unknown): ImageValue | null {
    const url = str(obj(v).url)
    return url ? { url } : null
}

/** Giá trị rỗng của một trường (dòng mới thêm vào danh sách). */
export function emptyValue(fields: SettingField[]): Obj {
    return toForm(fields, {})
}

export function toForm(fields: SettingField[], data: unknown): Obj {
    const d = obj(data)
    const out: Obj = {}
    for (const f of fields) {
        const v = d[f.k]
        switch (f.t) {
            case 'line':
            case 'text':
            case 'icon':
                out[f.k] = str(v)
                break
            case 'image':
                out[f.k] = imageIn(v)
                break
            case 'link':
                out[f.k] = linkIn(v)
                break
            case 'lines':
                out[f.k] = arr(v).map((x): LineRow => ({ v: str(x) }))
                break
            case 'list':
                out[f.k] = arr(v).map((x) => toForm(f.fields, x))
                break
            case 'group':
                out[f.k] = toForm(f.fields, v)
                break
        }
    }
    return out
}

const collapse = (s: string) => s.trim().split(/\s+/).filter(Boolean).join(' ')

export function toData(fields: SettingField[], form: unknown): Obj {
    const d = obj(form)
    const out: Obj = {}
    for (const f of fields) {
        const v = d[f.k]
        switch (f.t) {
            case 'line': {
                const s = collapse(str(v))
                out[f.k] = s === '' && !f.required ? null : s
                break
            }
            case 'text': {
                const s = str(v).trim()
                out[f.k] = s === '' ? null : s
                break
            }
            case 'icon':
                out[f.k] = str(v)
                break
            case 'image': {
                const img = imageIn(v)
                out[f.k] = img ? { url: img.url } : null
                break
            }
            case 'link':
                out[f.k] = linkOut(v as LinkForm) ?? null
                break
            case 'lines':
                out[f.k] = arr(v).map((x) => collapse(str(obj(x).v)))
                break
            case 'list':
                out[f.k] = arr(v).map((x) => toData(f.fields, x))
                break
            case 'group':
                out[f.k] = toData(f.fields, v)
                break
        }
    }
    return out
}

/**
 * Đường dẫn lỗi server (bỏ "data.") → tên ô trong form. Ô danh sách chữ lưu
 * dạng {v}, nên "usp_items.1" thành "usp_items.1.v".
 */
export function formPath(fields: SettingField[], serverField: string): string {
    const parts = serverField
        .replace(/^data\.?/, '')
        .split('.')
        .filter(Boolean)
    const out: string[] = []
    let scope: SettingField[] | null = fields
    for (let i = 0; i < parts.length; i++) {
        const f: SettingField | undefined = scope?.find((x) => x.k === parts[i])
        out.push(parts[i])
        if (!f) {
            scope = null
            continue
        }
        if (f.t === 'lines' && i + 1 < parts.length) {
            out.push(parts[++i], 'v')
            scope = null
        } else if (f.t === 'list' && i + 1 < parts.length) {
            out.push(parts[++i])
            scope = f.fields
        } else if (f.t === 'group') {
            scope = f.fields
        } else {
            scope = null
        }
    }
    return out.join('.') || 'root.server'
}

// --- kiểm ở FE (cùng luật cơ bản với server; lỗi chi tiết hơn do server trả) ---

const tooLong = (max: number) =>
    m.error_shop_field_too_long({ max: String(max) })

function textSchema(max: number, required: boolean, multiline: boolean) {
    return z.string().superRefine((v, ctx) => {
        const s = multiline ? v.trim() : collapse(v)
        if (required && !s)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: m.error_shop_field_required(),
            })
        else if ([...s].length > max)
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: tooLong(max) })
    })
}

function maxItems<T extends z.ZodTypeAny>(item: T, max: number) {
    return z.array(item).max(max, {
        message: m.error_shop_field_too_many({ max: String(max) }),
    })
}

function fieldSchema(f: SettingField): z.ZodTypeAny {
    switch (f.t) {
        case 'line':
            return textSchema(f.max, !!f.required, false)
        case 'text':
            return textSchema(f.max, false, true)
        case 'icon':
            return z
                .string()
                .refine(
                    (v) => (SITE_ICON_KEYS as readonly string[]).includes(v),
                    {
                        message: m.siteSettings_error_icon(),
                    },
                )
        case 'link':
            return f.required
                ? z.custom<LinkForm>().superRefine((l, ctx) => {
                      if (!l?.type)
                          ctx.addIssue({
                              code: z.ZodIssueCode.custom,
                              path: ['type'],
                              message: m.siteSettings_error_link_required(),
                          })
                  })
                : z.any()
        case 'image':
            return z.any()
        case 'lines':
            return maxItems(
                z.object({ v: textSchema(f.itemMax, true, false) }),
                f.max,
            )
        case 'list':
            return maxItems(settingSchema(f.fields), f.max)
        case 'group':
            return settingSchema(f.fields)
    }
}

/** Schema zod của một kind (factory để i18n đánh giá lúc dùng). */
export function settingSchema(fields: SettingField[]): z.ZodTypeAny {
    const shape: Record<string, z.ZodTypeAny> = {}
    for (const f of fields) shape[f.k] = fieldSchema(f)
    return z.object(shape).passthrough()
}

export function kindLabel(kind: ShopSiteSettingKind): string {
    switch (kind) {
        case 'brand':
            return m.siteSettings_tab_brand()
        case 'header':
            return m.siteSettings_tab_header()
        case 'footer':
            return m.siteSettings_tab_footer()
        case 'socials':
            return m.siteSettings_tab_socials()
        case 'seo':
            return m.siteSettings_tab_seo()
        case 'product_page':
            return m.siteSettings_tab_product_page()
        case 'checkout':
            return m.siteSettings_tab_checkout()
        case 'home_extra':
            return m.siteSettings_tab_home_extra()
        case 'ocop':
            return m.siteSettings_tab_ocop()
        case 'miniapp':
            return m.siteSettings_tab_miniapp()
    }
}
